import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Search,
    Eye,
    RefreshCw,
    Filter,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Car,
    Home,
    CheckCircle,
    XCircle,
    Clock,
    User,
    Building2,
    Store,
    TrendingUp,
    AlertCircle,
    RotateCcw,
    Trash2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
    useGetDealersQuery,
    useGetDealerStatsQuery,
    useUpdateDealerStatusMutation,
    useVerifyDealerMutation,
    useBulkDeleteDealersMutation,
    useRestoreDealerMutation,
    Dealer,
    DealersParams
} from '../../store/slices/apiSlice';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';

const DealerManagement: React.FC = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
    const [dealerTypeFilter, setDealerTypeFilter] = useState<'all' | 'car' | 'house'>('all');
    const [deletedFilter, setDeletedFilter] = useState<'active' | 'deleted' | 'all'>('active');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    // Modal states
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);
    const [newStatus, setNewStatus] = useState<boolean>(true);
    const [adminNotes, setAdminNotes] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');

    const [selectedDealerIds, setSelectedDealerIds] = useState<number[]>([]);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
    const [deleteType, setDeleteType] = useState<'soft' | 'hard'>('soft');
    const [bulkDeleteDealers, { isLoading: isBulkDeleting }] = useBulkDeleteDealersMutation();
    const [restoreDealer, { isLoading: isRestoring }] = useRestoreDealerMutation();

    const location = useLocation();


    // Fetch dealers
    const { data, isLoading, error, refetch } = useGetDealersQuery({
        page: currentPage,
        limit: itemsPerPage,
        verification_status: statusFilter === 'all' ? undefined : statusFilter,
        dealer_type: dealerTypeFilter === 'all' ? undefined : dealerTypeFilter,
        search: searchTerm || undefined,
        deleted_status: deletedFilter,
    }, {
        refetchOnMountOrArgChange: true,
    });

    // Fetch stats
    const { data: statsData, refetch: refetchStats } = useGetDealerStatsQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });

    // Refetch when returning from dealer details page
    useEffect(() => {
        const handleFocus = () => {
            refetch();
            refetchStats();
        };

        // Refetch when component mounts or location changes (coming back from details page)
        if (location.pathname === '/admin/dealer-management') {
            refetch();
            refetchStats();
        }

        // Also refetch when window gains focus
        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [location.pathname, refetch, refetchStats]);

    const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateDealerStatusMutation();
    const [verifyDealer, { isLoading: isVerifying }] = useVerifyDealerMutation();

    const handleSelectDealer = (dealerId: number, checked: boolean) => {
        if (checked) {
            setSelectedDealerIds(prev => [...prev, dealerId]);
        } else {
            setSelectedDealerIds(prev => prev.filter(id => id !== dealerId));
        }
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds = dealers.map(d => d.dealer_id).filter(id => id !== undefined);
            setSelectedDealerIds(allIds);
        } else {
            setSelectedDealerIds([]);
        }
    };

    const handleBulkDelete = async () => {
        try {
            await bulkDeleteDealers({
                dealer_ids: selectedDealerIds,
                delete_type: deleteType
            }).unwrap();

            toast.success(
                `Successfully ${deleteType === 'soft' ? 'soft-deleted' : 'hard-deleted'} ${selectedDealerIds.length} ${selectedDealerIds.length === 1 ? 'dealer' : 'dealers'}.`
            );
            setSelectedDealerIds([]);
            setIsBulkDeleteModalOpen(false);
            refetch();
            refetchStats();
        } catch (error: any) {
            console.error('Bulk delete dealers failed:', error);
            const errorMessage = error?.data?.message || 'Failed to complete deletion. Please try again.';
            toast.error(errorMessage);
        }
    };

    const handleRestoreDealer = async (dealerId: number) => {
        try {
            await restoreDealer({ dealer_id: dealerId }).unwrap();
            toast.success('Dealer restored successfully!');
            refetch();
            refetchStats();
        } catch (error: any) {
            console.error('Restore dealer failed:', error);
            const errorMessage = error?.data?.message || 'Failed to restore dealer. Please try again.';
            toast.error(errorMessage);
        }
    };

    useEffect(() => {
        setSelectedDealerIds([]);
    }, [searchTerm, statusFilter, dealerTypeFilter, deletedFilter, data]);


    // Extract dealers from API response
    const dealers = data?.data?.dealers || [];
    const pagination = data?.data?.pagination || { current_page: 1, total_pages: 1, total_records: 0 };
    const stats = statsData?.data || {
        total_dealers: 0,
        pending_dealers: 0,
        approved_dealers: 0,
        rejected_dealers: 0,
        car_dealers: 0,
        house_dealers: 0,
        total_dealer_listings: 0,
        active_dealer_listings: 0,
        sold_dealer_listings: 0,
    };

    // Helper function to get page numbers for pagination
    const getPageNumbers = () => {
        const pages: number[] = [];
        const maxVisiblePages = 5;
        const totalPages = pagination.total_pages;
        
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
            
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
        }
        
        return pages;
    };

    // Handle view - navigate to details page
    const handleView = (dealer: Dealer) => {
        navigate(`/admin/dealer-management/${dealer.dealer_id}`);
    };

    // Handle status modal open
    const handleStatusModalOpen = (dealer: Dealer) => {
        setSelectedDealer(dealer);
        setNewStatus(!dealer.is_active);
        setShowStatusModal(true);
    };

    // Handle status update
    const handleStatusUpdate = async () => {
        if (!selectedDealer) return;

        try {
            await updateStatus({
                dealer_id: selectedDealer.dealer_id,
                is_active: newStatus,
            }).unwrap();
            toast.success(`Dealer ${newStatus ? 'activated' : 'deactivated'} successfully!`);
            setShowStatusModal(false);
            setSelectedDealer(null);
            refetch();
            refetchStats();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to update dealer status');
        }
    };

    // Handle approve
    const handleApprove = (dealer: Dealer) => {
        setSelectedDealer(dealer);
        setAdminNotes('');
        setShowApproveModal(true);
    };

    // Handle reject
    const handleReject = (dealer: Dealer) => {
        setSelectedDealer(dealer);
        setRejectionReason('');
        setShowRejectModal(true);
    };

    // Submit approval
    const handleApproveSubmit = async () => {
        if (!selectedDealer) return;

        try {
            await verifyDealer({
                dealer_id: selectedDealer.dealer_id,
                status: 'approved',
                admin_notes: adminNotes.trim() || undefined,
            }).unwrap();
            toast.success('Dealer approved successfully!');
            setShowApproveModal(false);
            setSelectedDealer(null);
            setAdminNotes('');
            refetch();
            refetchStats();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to approve dealer');
        }
    };

    // Submit rejection
    const handleRejectSubmit = async () => {
        if (!selectedDealer) return;

        if (!rejectionReason.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }

        try {
            await verifyDealer({
                dealer_id: selectedDealer.dealer_id,
                status: 'rejected',
                admin_notes: rejectionReason.trim(),
            }).unwrap();
            toast.success('Dealer rejected successfully!');
            setShowRejectModal(false);
            setSelectedDealer(null);
            setRejectionReason('');
            refetch();
            refetchStats();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to reject dealer');
        }
    };

    // Get status badge
    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Approved</Badge>;
            case 'pending':
                return <Badge variant="warning" className="flex items-center gap-1"><Clock className="w-3 h-3" />Pending</Badge>;
            case 'rejected':
                return <Badge variant="danger" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Rejected</Badge>;
            default:
                return <Badge variant="secondary">{status}</Badge>;
        }
    };

    // Get dealer type icon
    const getDealerTypeIcon = (type: string) => {
        return type === 'car' ? <Car className="h-4 w-4" /> : <Home className="h-4 w-4" />;
    };

    // Format date
    const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center">
                        <XCircle className="h-6 w-6 text-red-600 mr-3" />
                        <div>
                            <h3 className="text-lg font-semibold text-red-800">Error Loading Dealers</h3>
                            <p className="text-red-600 mt-1">There was an error loading the dealers. Please try again.</p>
                            <button
                                onClick={() => refetch()}
                                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dealer Management</h1>
                        <p className="text-gray-600">Manage and verify dealer accounts</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => refetch()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics Cards (Only 3 boxes) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Dealers</p>
                            <p className="text-3xl font-bold text-gray-900">{stats.total_dealers}</p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                            <User className="h-6 w-6 text-blue-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Pending</p>
                            <p className="text-3xl font-bold text-yellow-600">{stats.pending_dealers}</p>
                        </div>
                        <div className="p-3 bg-yellow-100 rounded-full">
                            <Clock className="h-6 w-6 text-yellow-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Approved</p>
                            <p className="text-3xl font-bold text-green-600">{stats.approved_dealers}</p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-full">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Search dealers by name or email..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value as any);
                                    setCurrentPage(1);
                                }}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>

                        <div className="relative">
                            <select
                                value={dealerTypeFilter}
                                onChange={(e) => {
                                    setDealerTypeFilter(e.target.value as any);
                                    setCurrentPage(1);
                                }}
                                className="pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Types</option>
                                <option value="car">Car Dealers</option>
                                <option value="house">House Dealers</option>
                            </select>
                        </div>

                        <div className="relative">
                            <select
                                value={deletedFilter}
                                onChange={(e) => {
                                    setDeletedFilter(e.target.value as any);
                                    setCurrentPage(1);
                                }}
                                className="pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="active">Active Dealers</option>
                                <option value="deleted">Soft Deleted Dealers</option>
                                <option value="all">All Dealers</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dealers Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Dealers ({pagination.total_records})
                        </h2>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Items per page:</label>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                    </div>
                </div>

                {dealers.length === 0 ? (
                    <div className="p-12 text-center">
                        <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No dealers found</h3>
                        <p className="text-gray-600">No dealers match your current filters.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left w-10">
                                        <input
                                            type="checkbox"
                                            checked={dealers.length > 0 && selectedDealerIds.length === dealers.length}
                                            ref={(input) => {
                                                if (input) {
                                                    input.indeterminate = selectedDealerIds.length > 0 && selectedDealerIds.length < dealers.length;
                                                }
                                            }}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                                        />
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {dealers.map((dealer) => (
                                    <tr key={dealer.dealer_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 w-10">
                                            <input
                                                type="checkbox"
                                                checked={selectedDealerIds.includes(dealer.dealer_id)}
                                                onChange={(e) => handleSelectDealer(dealer.dealer_id, e.target.checked)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden mr-3">
                                                    {dealer.profile_image ? (
                                                        <img
                                                            src={dealer.profile_image}
                                                            alt={dealer.name}
                                                            className="h-full w-full object-cover"
                                                        />
                                                    ) : (
                                                        <User className="h-5 w-5 text-gray-400" />
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{dealer.name}</div>
                                                    <div className="text-sm text-gray-500">{dealer.email}</div>
                                                    {dealer.mobile_number && (
                                                        <div className="text-xs text-gray-400">{dealer.mobile_number}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">{dealer.business_name}</div>
                                            {dealer.business_address && (
                                                <div className="text-sm text-gray-500 truncate max-w-xs">{dealer.business_address}</div>
                                            )}
                                            {dealer.business_phone && (
                                                <div className="text-xs text-gray-400">{dealer.business_phone}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                {getDealerTypeIcon(dealer.dealer_type)}
                                                <span className="text-sm text-gray-900 capitalize">{dealer.dealer_type}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getStatusBadge(dealer.verification_status)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {dealer.is_deleted ? (
                                                <Badge variant="danger" className="flex items-center gap-1 w-fit">
                                                    <XCircle className="w-3 h-3" />Deleted
                                                </Badge>
                                            ) : dealer.is_active ? (
                                                <Badge variant="success" className="flex items-center gap-1 w-fit">
                                                    <CheckCircle className="w-3 h-3" />Active
                                                </Badge>
                                            ) : (
                                                <Badge variant="danger" className="flex items-center gap-1 w-fit">
                                                    <XCircle className="w-3 h-3" />Inactive
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(dealer.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                {dealer.is_deleted && (
                                                    <button
                                                        onClick={() => handleRestoreDealer(dealer.dealer_id)}
                                                        className="text-blue-600 hover:text-blue-700 p-2 hover:bg-blue-50 rounded-lg transition-all duration-200 border border-transparent hover:border-blue-300 hover:shadow-sm"
                                                        title="Restore Dealer"
                                                    >
                                                        <RotateCcw className="h-4 w-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleView(dealer)}
                                                    className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                                    title="View Details"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                {dealer.verification_status === 'pending' && !dealer.is_deleted && (
                                                    <>
                                                        <button
                                                            onClick={() => handleApprove(dealer)}
                                                            className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors border border-transparent hover:border-green-200"
                                                            title="Approve"
                                                        >
                                                            <CheckCircle className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleReject(dealer)}
                                                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                                                            title="Reject"
                                                        >
                                                            <XCircle className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700">
                                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                                <span className="font-medium">
                                    {Math.min(currentPage * itemsPerPage, pagination.total_records)}
                                </span>{' '}
                                of <span className="font-medium">{pagination.total_records}</span> dealers
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    <ChevronsLeft className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                {getPageNumbers().map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`px-3 py-2 border rounded-lg ${
                                            currentPage === page
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === pagination.total_pages}
                                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => setCurrentPage(pagination.total_pages)}
                                    disabled={currentPage === pagination.total_pages}
                                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    <ChevronsRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Status Update Modal */}
            <Modal
                isOpen={showStatusModal}
                onClose={() => {
                    setShowStatusModal(false);
                    setSelectedDealer(null);
                }}
                title={`${newStatus ? 'Activate' : 'Deactivate'} Dealer`}
            >
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to {newStatus ? 'activate' : 'deactivate'} the dealer{' '}
                        <span className="font-semibold">{selectedDealer?.name}</span>?
                    </p>
                    <div className="flex justify-end gap-3 mt-6">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowStatusModal(false);
                                setSelectedDealer(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant={newStatus ? 'success' : 'danger'}
                            onClick={handleStatusUpdate}
                            disabled={isUpdatingStatus}
                        >
                            {isUpdatingStatus ? 'Updating...' : `${newStatus ? 'Activate' : 'Deactivate'}`}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Approve Modal */}
            <Modal
                isOpen={showApproveModal}
                onClose={() => {
                    setShowApproveModal(false);
                    setSelectedDealer(null);
                    setAdminNotes('');
                }}
                title="Approve Dealer"
            >
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to approve the dealer{' '}
                        <span className="font-semibold">{selectedDealer?.name}</span>?
                    </p>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Admin Notes (Optional)
                        </label>
                        <textarea
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Add any notes about this approval..."
                        />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowApproveModal(false);
                                setSelectedDealer(null);
                                setAdminNotes('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="success"
                            onClick={handleApproveSubmit}
                            disabled={isVerifying}
                        >
                            {isVerifying ? 'Approving...' : 'Approve'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Reject Modal */}
            <Modal
                isOpen={showRejectModal}
                onClose={() => {
                    setShowRejectModal(false);
                    setSelectedDealer(null);
                    setRejectionReason('');
                }}
                title="Reject Dealer"
            >
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to reject the dealer{' '}
                        <span className="font-semibold">{selectedDealer?.name}</span>?
                    </p>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Rejection Reason <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Please provide a reason for rejection..."
                            required
                        />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowRejectModal(false);
                                setSelectedDealer(null);
                                setRejectionReason('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleRejectSubmit}
                            disabled={isVerifying || !rejectionReason.trim()}
                        >
                            {isVerifying ? 'Rejecting...' : 'Reject'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Floating Bulk Actions Bar */}
            {selectedDealerIds.length > 0 && (
                <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-4 rounded-2xl shadow-2xl z-50 flex items-center gap-6 border border-gray-800 transition-all duration-300">
                    <span className="text-sm font-semibold">
                        {selectedDealerIds.length} {selectedDealerIds.length === 1 ? 'dealer' : 'dealers'} selected
                    </span>
                    <div className="h-6 w-px bg-gray-800"></div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => {
                                setDeleteType('soft');
                                setIsBulkDeleteModalOpen(true);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                        >
                            Soft Delete
                        </button>
                        <button
                            onClick={() => {
                                setDeleteType('hard');
                                setIsBulkDeleteModalOpen(true);
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                        >
                            Hard Delete
                        </button>
                        <button
                            onClick={() => setSelectedDealerIds([])}
                            className="text-gray-400 hover:text-white px-2 py-2 text-xs font-semibold transition-colors duration-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            <Modal
                isOpen={isBulkDeleteModalOpen}
                onClose={() => setIsBulkDeleteModalOpen(false)}
                title={`${deleteType === 'soft' ? 'Soft' : 'Hard'} Delete Selected Dealers`}
            >
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to {deleteType === 'soft' ? 'soft delete' : 'permanently hard delete'}{' '}
                        <span className="font-semibold text-gray-900">{selectedDealerIds.length}</span> selected {selectedDealerIds.length === 1 ? 'dealer' : 'dealers'}?
                    </p>
                    {deleteType === 'hard' && (
                        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs sm:text-sm font-medium mb-6">
                            <span className="font-bold uppercase mt-0.5">Warning:</span>
                            <span>
                                This action is permanent and cannot be undone. All listings, images, inquiries, and transactions associated with these dealer accounts will be permanently destroyed.
                            </span>
                        </div>
                    )}
                    <div className="flex justify-end gap-3 mt-6">
                        <Button
                            variant="outline"
                            onClick={() => setIsBulkDeleteModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant={deleteType === 'soft' ? 'warning' : 'danger'}
                            onClick={handleBulkDelete}
                            disabled={isBulkDeleting}
                        >
                            {isBulkDeleting ? 'Deleting...' : `Confirm ${deleteType === 'soft' ? 'Soft' : 'Hard'} Delete`}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DealerManagement;

